/* global Set */
/**
 * "Filter by Portfolio Holdings": targets users by what they hold today.
 * Sent as `audience.portfolioHoldings` (NotificationPortfolioHoldingsInput) on
 * create and edit; the audience is resolved when the notification goes out.
 *
 * The picker stores instruments and collections in one field, as antd
 * labelInValue entries whose value is `KIND:id`, so the chip keeps its ticker
 * and the payload can split them into `instrumentIds` / `collectionIds`.
 */

/** Form audience values the backend accepts holdings filters with. */
export const HOLDINGS_AUDIENCES = [
  'ALL_USERS',
  'USERS',
  'SPECIFIC_USERS',
  'INTERNAL_TEAM_MEMBERS',
  'USERS_BY_GROUP',
];

export const supportsHoldings = audience =>
  HOLDINGS_AUDIENCES.includes(audience || 'ALL_USERS');

export const HOLDINGS_EXCHANGES = ['BRVM', 'NGX'];

export const HOLDINGS_FIELDS = [
  'holdingsAssetClasses',
  'holdingsItems',
  'holdingsExchanges',
  'holdingsOperator',
  'holdingsMinAmount',
  'holdingsMinCurrency',
  'holdingsIncludeUnsettled',
];

const list = value => [...new Set([].concat(value ?? []).filter(Boolean))];

export const itemValue = (kind, id) => `${kind}:${id}`;

const idsOfKind = (items, kind) =>
  list(
    [].concat(items ?? []).map(item => {
      const [itemKind, id] = String(item?.value ?? item).split(':');
      return itemKind === kind ? id : null;
    }),
  );

/** "0" and "100.5" count; empty means the threshold is off. */
const amountOf = value => {
  const text = String(value ?? '').trim();
  return /^\d+(\.\d+)?$/.test(text) ? Number(text) : null;
};

export const isHoldingsAmount = value =>
  !String(value ?? '').trim() || amountOf(value) !== null;

/** Classes, instruments, collections and exchanges, each one a selection. */
export const holdingsSelectionCount = values =>
  list(values?.holdingsAssetClasses).length +
  list(values?.holdingsExchanges).length +
  [].concat(values?.holdingsItems ?? []).length;

/** The AND/OR toggle only means something with two or more selections. */
export const showHoldingsOperator = values =>
  holdingsSelectionCount(values) >= 2;

/**
 * `{portfolioHoldings}` for the audience, or `{}` when nothing is chosen or
 * the audience does not take holdings filters (the API rejects them there).
 */
export const buildHoldingsAudience = values => {
  if (!supportsHoldings(values?.audience)) return {};
  if (!holdingsSelectionCount(values)) return {};

  const holdings = {
    includeUnsettled: Boolean(values.holdingsIncludeUnsettled),
  };
  if (showHoldingsOperator(values)) {
    holdings.operator = values.holdingsOperator === 'AND' ? 'AND' : 'OR';
  }
  const assetClasses = list(values.holdingsAssetClasses);
  const instrumentIds = idsOfKind(values.holdingsItems, 'INSTRUMENT');
  const collectionIds = idsOfKind(values.holdingsItems, 'COLLECTION');
  const exchanges = list(values.holdingsExchanges);
  if (assetClasses.length) holdings.assetClasses = assetClasses;
  if (instrumentIds.length) holdings.instrumentIds = instrumentIds;
  if (collectionIds.length) holdings.collectionIds = collectionIds;
  if (exchanges.length) holdings.exchanges = exchanges;

  const amount = amountOf(values.holdingsMinAmount);
  if (amount !== null) {
    holdings.minHoldingValue = {
      amount,
      currency: values.holdingsMinCurrency || 'XOF',
    };
  }
  return {portfolioHoldings: holdings};
};

/** A saved `audience.portfolioHoldings`, back as form values. */
export const holdingsAudienceToForm = holdings => {
  if (!holdings) return {};
  const items = [
    ...(holdings.instruments ?? []).map(instrument => ({
      value: itemValue('INSTRUMENT', instrument.id),
      label: instrument.ticker || instrument.name || instrument.id,
    })),
    ...(holdings.collectionIds ?? []).map(id => ({
      value: itemValue('COLLECTION', id),
      label: `Collection …${String(id).slice(-6)}`,
    })),
  ];
  const min = holdings.minHoldingValue;
  return {
    holdingsAssetClasses: holdings.assetClasses?.length
      ? holdings.assetClasses
      : undefined,
    holdingsItems: items.length ? items : undefined,
    holdingsExchanges: holdings.exchanges?.length
      ? holdings.exchanges
      : undefined,
    holdingsOperator: holdings.operator || 'OR',
    holdingsMinAmount:
      min?.amount === null || min?.amount === undefined
        ? undefined
        : String(min.amount),
    holdingsMinCurrency: min?.currency || 'XOF',
    holdingsIncludeUnsettled: Boolean(holdings.includeUnsettled),
  };
};

/**
 * `HoldingsAudiencePreviewInput` from a notification payload: the whole
 * audience, plus recipients for specific users and groups for a group send.
 */
export const toHoldingsPreviewInput = payload => {
  const category = payload?.audience?.category;
  return {
    audience: payload?.audience,
    userIds: category === 'SPECIFIC_USERS' ? payload?.userIds ?? [] : [],
    userEmails: category === 'SPECIFIC_USERS' ? payload?.userEmails ?? [] : [],
    groupIds: category === 'USERS_BY_GROUP' ? payload?.groups ?? [] : [],
  };
};
